/**
 * PensionPage — UK Pension Contribution Optimiser
 *
 * Answers: "How much more can I put into my SIPP this year, and should I
 * actually do it vs topping up the ISA?"
 *
 * Covers the 2026/27 rules: £60k standard AA, carry forward from prior 3
 * years, tapered AA if adjusted income > £260k, MPAA if already flexibly
 * accessed a DC pension, £20k ISA allowance, and — crucially — the April
 * 2027 change where SIPPs become IHT-includable.
 */
import { useEffect, useRef, useState } from 'react'
import {
  PiggyBank, TrendingUp, AlertTriangle, Sparkles, RefreshCw, ArrowRight,
  ShieldCheck, Coins,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import PensionsTabs from '@/components/ui/PensionsTabs'

interface PensionAction {
  priority: number
  action: string
  amount_gbp: number
  tax_saving_gbp: number
  reasoning: string
}

interface PathInfo {
  label: string
  contribution_gbp: number
  tax_relief_gbp: number
  gross_in_wrapper_gbp: number
  access_age: number
  tax_free_lump_sum_cap_gbp: number | null
  drawdown_taxable: boolean
  note: string
}

interface PensionOptimiseResult {
  current_pension_value_gbp: number
  current_isa_value_gbp: number
  tax_band: string
  marginal_rate_pct: number
  annual_income_gbp: number
  standard_aa_gbp: number
  tapered_aa_gbp: number
  taper_applies: boolean
  mpaa_active: boolean
  mpaa_gbp: number | null
  effective_aa_gbp: number
  already_contributed_gbp: number
  aa_remaining_this_year_gbp: number
  prior_year_unused_gbp: number[]
  carry_forward_available_gbp: number
  total_room_gbp: number
  isa_annual_allowance_gbp: number
  isa_contributed_gbp: number
  isa_room_gbp: number
  tax_relief_at_max_pension_gbp: number
  tax_relief_at_max_isa_gbp: number
  sipp_path: PathInfo
  isa_path: PathInfo
  recommendation: string
  actions: PensionAction[]
  april_2027_warning: boolean
  april_2027_note: string
  assumptions: Record<string, any>
}

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtCompact = (n: number) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

const BAND_COLOURS: Record<string, string> = {
  additional:       'text-emerald bg-emerald/10 border-emerald/40',
  higher:           'text-blue-light bg-blue/10 border-blue/30',
  basic:            'text-amber bg-amber/10 border-amber/30',
  below_threshold:  'text-text-muted bg-surface-card border-border',
}

const BAND_LABEL: Record<string, string> = {
  additional:       'Additional rate (45%)',
  higher:           'Higher rate (40%)',
  basic:            'Basic rate (20%)',
  below_threshold:  'Below tax threshold',
}

export default function PensionPage() {
  // Inputs — Simon defaults
  const [income, setIncome] = useState(220_000)  // Simon £210k base + ~£10k bonus
  const [alreadyContributed, setAlreadyContributed] = useState(10_000)
  const [isaContributed, setIsaContributed] = useState(0)
  const [prior1, setPrior1] = useState(60_000)
  const [prior2, setPrior2] = useState(60_000)
  const [prior3, setPrior3] = useState(60_000)
  const [mpaa, setMpaa] = useState(false)

  const [data, setData] = useState<PensionOptimiseResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track whether the component has completed its initial mount fetch so the
  // slider-change effect doesn't fire a redundant second call on first render.
  const hasMounted = useRef(false)

  const fetchOptimise = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<PensionOptimiseResult>('/api/pension/optimise', {
        params: {
          annual_income: income,
          already_contributed: alreadyContributed,
          prior_year_unused: `[${prior1},${prior2},${prior3}]`,
          isa_contributed: isaContributed,
          already_accessed_pension: mpaa,
        },
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load pension optimisation')
    } finally {
      setLoading(false)
    }
  }

  // Initial fetch on mount.
  useEffect(() => {
    fetchOptimise()
    hasMounted.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced re-fetch when sliders change — skip the very first render because
  // the mount effect above already fired.
  useEffect(() => {
    if (!hasMounted.current) return
    const t = setTimeout(fetchOptimise, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [income, alreadyContributed, isaContributed, prior1, prior2, prior3, mpaa])

  const bandColour = data ? (BAND_COLOURS[data.tax_band] ?? BAND_COLOURS.basic) : ''
  const bandLabel = data ? (BAND_LABEL[data.tax_band] ?? data.tax_band) : ''

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <PiggyBank size={20} className="text-blue-light" aria-hidden />
            Pension Contribution Optimiser
          </h1>
          <p className="text-sm text-text-secondary">
            How much SIPP room do you have, and should you actually use it?
          </p>
        </div>
        <button type="button" onClick={fetchOptimise} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <PensionsTabs />

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Current position */}
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div className="stat-card">
            <p className="stat-label">Current SIPP value</p>
            <p className="stat-value text-blue-light">{fmt(data.current_pension_value_gbp)}</p>
            <p className="text-xs text-text-muted">all tracked SIPP accounts</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Current ISA value</p>
            <p className="stat-value text-emerald-light">{fmt(data.current_isa_value_gbp)}</p>
            <p className="text-xs text-text-muted">all tracked ISA accounts</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Tax band</p>
            <p className="stat-value">{data.marginal_rate_pct}%</p>
            <p className="text-xs text-text-muted">{bandLabel}</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Effective AA</p>
            <p className="stat-value">{fmt(data.effective_aa_gbp)}</p>
            <p className="text-xs text-text-muted">
              {data.mpaa_active ? 'MPAA active' : data.taper_applies ? 'tapered' : 'standard'}
            </p>
          </div>
        </div>
      )}

      {/* Headline — total room + tax relief */}
      {data && (
        <div className={clsx('card p-6 border-2', bandColour)}>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="panel-title opacity-80">
                Total pension room available
              </p>
              <h2 className="stat-hero text-gradient mt-1">
                {fmt(data.total_room_gbp)}
              </h2>
              <p className="text-sm text-text-secondary mt-2">
                {fmt(data.aa_remaining_this_year_gbp)} remaining this year
                {data.carry_forward_available_gbp > 0 && (
                  <> + {fmt(data.carry_forward_available_gbp)} carry forward</>
                )}
              </p>
              <div className="flex items-center gap-6 mt-4 flex-wrap text-sm">
                <div>
                  <span className="text-text-muted">Already contributed: </span>
                  <span className="font-bold text-text-primary">{fmt(data.already_contributed_gbp)}</span>
                </div>
                <div>
                  <span className="text-text-muted">ISA room: </span>
                  <span className="font-bold text-text-primary">{fmt(data.isa_room_gbp)}</span>
                </div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs text-text-muted uppercase tracking-wider">Tax relief if maxed</p>
              <div className="text-4xl font-bold text-emerald-light mt-1">
                {fmt(data.tax_relief_at_max_pension_gbp)}
              </div>
              <p className="text-xs text-text-muted mt-1">
                at {data.marginal_rate_pct}% marginal rate
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Your situation
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberSlider
            label="Annual income"
            value={income}
            onChange={setIncome}
            min={20_000}
            max={500_000}
            step={5_000}
            format={fmt}
          />
          <NumberSlider
            label="SIPP contributed this tax year"
            value={alreadyContributed}
            onChange={setAlreadyContributed}
            min={0}
            max={240_000}
            step={1_000}
            format={fmt}
          />
          <NumberSlider
            label="ISA used this tax year"
            value={isaContributed}
            onChange={setIsaContributed}
            min={0}
            max={20_000}
            step={500}
            format={fmt}
          />
          <NumberSlider
            label="Prior year 1 (most recent) unused"
            value={prior1}
            onChange={setPrior1}
            min={0}
            max={60_000}
            step={1_000}
            format={fmt}
          />
          <NumberSlider
            label="Prior year 2 unused"
            value={prior2}
            onChange={setPrior2}
            min={0}
            max={60_000}
            step={1_000}
            format={fmt}
          />
          <NumberSlider
            label="Prior year 3 (oldest) unused"
            value={prior3}
            onChange={setPrior3}
            min={0}
            max={60_000}
            step={1_000}
            format={fmt}
          />
          <label className="flex items-center gap-2 text-xs text-text-secondary self-end col-span-2 sm:col-span-1">
            <input
              type="checkbox"
              checked={mpaa}
              onChange={(e) => setMpaa(e.target.checked)}
              className="rounded border-border bg-surface-card"
            />
            Already flexibly accessed a DC pension (MPAA)
          </label>
        </div>
      </div>

      {/* SIPP vs ISA side-by-side */}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card p-5 border-blue/30 bg-blue/5">
            <h3 className="panel-title mb-3 flex items-center gap-2">
              <PiggyBank size={14} className="text-blue-light" aria-hidden />
              {data.sipp_path.label}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-text-muted">You contribute</span>
                <span className="font-bold text-text-primary">{fmt(data.sipp_path.contribution_gbp)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Tax relief</span>
                <span className="font-bold text-emerald-light">+{fmt(data.sipp_path.tax_relief_gbp)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-text-muted">Gross in wrapper</span>
                <span className="font-bold text-blue-light">{fmt(data.sipp_path.gross_in_wrapper_gbp)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Access age</span>
                <span className="font-bold text-text-primary">{data.sipp_path.access_age}</span>
              </div>
              {data.sipp_path.tax_free_lump_sum_cap_gbp !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Tax-free lump sum cap</span>
                  <span className="font-bold text-text-primary">{fmt(data.sipp_path.tax_free_lump_sum_cap_gbp)}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-text-muted mt-3 italic">{data.sipp_path.note}</p>
          </div>

          <div className="card p-5 border-emerald/30 bg-emerald/5">
            <h3 className="panel-title mb-3 flex items-center gap-2">
              <ShieldCheck size={14} className="text-emerald-light" aria-hidden />
              {data.isa_path.label}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-text-muted">You contribute</span>
                <span className="font-bold text-text-primary">{fmt(data.isa_path.contribution_gbp)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Tax relief</span>
                <span className="font-bold text-text-muted">—</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-text-muted">Gross in wrapper</span>
                <span className="font-bold text-emerald-light">{fmt(data.isa_path.gross_in_wrapper_gbp)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Access age</span>
                <span className="font-bold text-text-primary">Any</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Lifetime cap</span>
                <span className="font-bold text-text-primary">None</span>
              </div>
            </div>
            <p className="text-xs text-text-muted mt-3 italic">{data.isa_path.note}</p>
          </div>
        </div>
      )}

      {/* Recommendation */}
      {data && (
        <div className="card p-5 border-blue/30 bg-blue/5">
          <h2 className="panel-title mb-2 flex items-center gap-2">
            <TrendingUp size={14} className="text-blue-light" aria-hidden />
            Recommendation
          </h2>
          <p className="text-sm text-text-secondary leading-relaxed">{data.recommendation}</p>
        </div>
      )}

      {/* Action list */}
      {data && data.actions.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3 flex items-center gap-2">
            <Coins size={14} className="text-amber" aria-hidden />
            Ordered actions
          </h2>
          <div className="space-y-3">
            {data.actions.map((a) => (
              <div key={a.priority} className="p-3 rounded-lg border border-border bg-surface-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="badge badge-blue text-[10px] shrink-0">#{a.priority}</span>
                    <span className="text-sm font-semibold text-text-primary">{a.action}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    {a.amount_gbp > 0 && (
                      <span className="text-text-primary font-bold">{fmt(a.amount_gbp)}</span>
                    )}
                    {a.tax_saving_gbp > 0 && (
                      <>
                        <ArrowRight size={12} className="text-text-muted" aria-hidden />
                        <span className="text-emerald-light font-bold">+{fmt(a.tax_saving_gbp)} relief</span>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-2 leading-relaxed">{a.reasoning}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Carry-forward breakdown */}
      {data && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">Annual allowance breakdown</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
            <div className="p-3 rounded-lg border border-border bg-surface-card">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Standard AA</p>
              <p className="font-bold text-text-primary mt-1">{fmt(data.standard_aa_gbp)}</p>
            </div>
            <div className={clsx(
              'p-3 rounded-lg border',
              data.taper_applies ? 'border-amber/30 bg-amber/5' : 'border-border bg-surface-card',
            )}>
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Tapered AA</p>
              <p className="font-bold text-text-primary mt-1">{fmt(data.tapered_aa_gbp)}</p>
              {data.taper_applies && <p className="text-[10px] text-amber mt-1">taper applies</p>}
            </div>
            <div className={clsx(
              'p-3 rounded-lg border',
              data.mpaa_active ? 'border-rose/30 bg-rose/5' : 'border-border bg-surface-card',
            )}>
              <p className="text-[10px] uppercase tracking-wider text-text-muted">MPAA</p>
              <p className="font-bold text-text-primary mt-1">
                {data.mpaa_active ? fmt(data.mpaa_gbp ?? 10_000) : '—'}
              </p>
              {data.mpaa_active && <p className="text-[10px] text-rose-light mt-1">active</p>}
            </div>
            <div className="p-3 rounded-lg border border-border bg-surface-card">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">This year left</p>
              <p className="font-bold text-text-primary mt-1">{fmt(data.aa_remaining_this_year_gbp)}</p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-surface-card">
              <p className="text-[10px] uppercase tracking-wider text-text-muted">Carry forward</p>
              <p className="font-bold text-text-primary mt-1">{fmt(data.carry_forward_available_gbp)}</p>
              <p className="text-[10px] text-text-muted mt-1">
                {data.prior_year_unused_gbp.map(fmtCompact).join(' + ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* April 2027 warning */}
      {data?.april_2027_warning && (
        <div className="card p-5 border-amber/40 bg-amber/10">
          <h2 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber" aria-hidden />
            April 2027 — SIPPs become IHT-includable
          </h2>
          <p className="text-xs text-text-secondary leading-relaxed">{data.april_2027_note}</p>
        </div>
      )}

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Methodology &amp; UK 2026/27 rules
        </summary>
        <div className="mt-3 space-y-2">
          <p><span className="text-text-primary font-semibold">Standard annual allowance:</span> £60,000 per tax year.</p>
          <p><span className="text-text-primary font-semibold">Carry forward:</span> unused allowance from the prior 3 tax years, up to a max of £60k each. Must have been a pension scheme member in those years. MPAA blocks carry forward entirely.</p>
          <p><span className="text-text-primary font-semibold">Tapered annual allowance:</span> reduced by £1 for every £2 of adjusted income over £260,000, floored at £10,000 — only if both adjusted income &gt; £260k AND threshold income &gt; £200k.</p>
          <p><span className="text-text-primary font-semibold">MPAA (Money Purchase AA):</span> £10,000 if you've already flexibly accessed a defined-contribution pension. No carry forward.</p>
          <p><span className="text-text-primary font-semibold">Tax relief:</span> basic 20% (automatic via relief-at-source), higher 40% (claim extra 20% via self-assessment), additional 45% (claim extra 25%).</p>
          <p><span className="text-text-primary font-semibold">Lifetime allowance:</span> abolished April 2024. Replaced by the Lump Sum Allowance (LSA) capping the tax-free lump sum at £268,275.</p>
          <p><span className="text-text-primary font-semibold">ISA:</span> £20,000 per person per tax year, no carry forward, no lifetime cap, fully tax-free, no tax relief on the way in.</p>
          <p className="pt-2 text-text-muted">
            Current SIPP and ISA values are pulled from the tracked-account list (AJ Bell SIPP
            Managed + XO for the pension; AJ Bell ISAs for Simon and Unman for the ISA). Tax bands
            assume rUK (England/Wales/NI), not Scotland. This is planning guidance, not regulated
            financial advice — double-check with your accountant before making large contributions.
          </p>
        </div>
      </details>
    </div>
  )
}

// ── Slider input component (same pattern as PlanningPage) ────────────────────
function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}
